/**
 * Frame3_1 - Estate Plan Overview Summary
 *
 * Purpose: High-level overview of the estate plan for executors to review
 * before beginning settlement workflow.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText, User, ArrowRight, Home } from 'lucide-react';
import { Button } from '../ui/Button';
import { useSettlementSession } from '../../hooks/useSettlementSession';

export default function Frame3_1() {
  const navigate = useNavigate();
  const { estateId } = useParams();
  const { loadPlannerData } = useSettlementSession(estateId || null);
  const [plannerInfo, setPlannerInfo] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!estateId) {
        setLoading(false);
        return;
      }

      try {
        // Load basic planner information
        const contactsData = await loadPlannerData('frame-146');
        setPlannerInfo(contactsData);
      } catch (error) {
        console.error('Failed to load planner data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [estateId, loadPlannerData]);

  const handleNext = () => {
    navigate(estateId ? `/frame-3-11/${estateId}` : '/frame-3-11');
  };

  const handleBack = () => {
    navigate(estateId ? `/frame-383/${estateId}` : '/frame-383');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading estate plan...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={handleBack}
            className="text-cyan-600 hover:text-cyan-700 mb-4 flex items-center gap-2"
          >
            <Home className="w-4 h-4" />
            Back to Welcome
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Estate Plan Overview</h1>
          <p className="text-gray-600">
            Review the estate plan created by the deceased to understand the settlement scope.
          </p>
        </div>

        {/* Plan Information Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-cyan-100 rounded-full p-3">
              <User className="w-6 h-6 text-cyan-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Estate Holder</h2>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-medium text-gray-900">
                    {plannerInfo?.personalInfo?.firstName} {plannerInfo?.personalInfo?.lastName}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date of Death:</span>
                  <span className="font-medium text-gray-900">
                    {plannerInfo?.dateOfDeath || 'Not specified'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <div className="flex items-start gap-4">
              <div className="bg-purple-100 rounded-full p-3">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Plan Status</h2>
                <p className="text-gray-600 mb-4">
                  The estate plan was created and is now ready for settlement execution. All
                  planning documents, asset information, and distribution instructions have been
                  preserved.
                </p>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-green-800 font-medium">Estate Plan Complete</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* What's Included Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            What's Included in This Plan
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Financial Assets</h3>
              <p className="text-sm text-gray-600">
                Bank accounts, investments, retirement funds, life insurance, and other financial
                assets
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Real Property</h3>
              <p className="text-sm text-gray-600">
                Real estate, vehicles, and other titled property
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Personal Property</h3>
              <p className="text-sm text-gray-600">
                Household items, valuables, collections, and digital assets
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Distribution Plan</h3>
              <p className="text-sm text-gray-600">
                Beneficiary assignments, percentages, and specific bequest instructions
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button variant="outline" onClick={handleBack}>
            Back
          </Button>
          <Button onClick={handleNext} className="flex items-center gap-2">
            View Assets Summary
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
